/**
 * Shared logic for roadmap language menu rows (stats + resume) — used by RoadmapView and Home continue card.
 */

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import SpacedRepetition from '../components/SRS/SpacedRepetition';
import * as roadmapService from '../services/roadmapService';
import { buildOrderedNodeGraph } from './roadmapGraph';
import {
  formatLanguageDisplay,
  formatRoadmapTrackTitle,
  ROADMAP_SCOPE_LABEL,
  trackMatchesLanguage,
  normalizeRoadmapLanguageKey,
  getTrackLanguageKey
} from './roadmapDisplay';
import { getRoadmapTrackIdFromUserDeckData } from './roadmapSubscription';

const ROADMAP_GRAPH_SCOPE_STORAGE_KEY = (uid) => (uid ? `flashcode_roadmap_graph_scope_${uid}` : null);

function readRoadmapGraphScopeMap(userId) {
  if (!userId || typeof window === 'undefined') return {};
  const k = ROADMAP_GRAPH_SCOPE_STORAGE_KEY(userId);
  if (!k) return {};
  try {
    const raw = window.localStorage.getItem(k);
    if (!raw) return {};
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

function readRoadmapGraphScopeForLanguage(userId, langKey) {
  const map = readRoadmapGraphScopeMap(userId);
  const sk = normalizeRoadmapLanguageKey(langKey);
  const v = map[sk];
  return v === 'learningPath' || v === 'nodes' ? v : null;
}

export const LAST_ROADMAP_LANG_KEY = (uid) => (uid ? `flashcode_roadmap_last_lang_${uid}` : null);

export function readStoredLastRoadmapLanguage(userId) {
  if (!userId || typeof window === 'undefined') return null;
  const k = LAST_ROADMAP_LANG_KEY(userId);
  if (!k) return null;
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}

export function availableLanguagesFromTracks(tracks) {
  const set = new Set();
  (tracks || []).forEach((t) => {
    const key = getTrackLanguageKey(t);
    if (key) set.add(key);
  });
  return Array.from(set).sort();
}

/** Default Learn language for guests with no stored last-opened roadmap language (prefer Python catalog). */
export function resolveGuestDefaultRoadmapLanguage(tracks) {
  const available = availableLanguagesFromTracks(tracks);
  if (!available.length) return null;
  const py = available.find((l) => normalizeRoadmapLanguageKey(l) === 'python');
  return py ?? available[0];
}

/** Catalog language string still present in tracks, or null */
export function resolvePreferredRoadmapLanguage(userId, tracks) {
  const available = availableLanguagesFromTracks(tracks);
  if (!available.length) return null;
  const stored = readStoredLastRoadmapLanguage(userId);
  if (!stored) return null;
  const sn = normalizeRoadmapLanguageKey(stored);
  return available.find((l) => normalizeRoadmapLanguageKey(l) === sn) || null;
}

/**
 * @param {string|null} userId
 * @param {string} lang - catalog language key
 * @param {object[]} tracks - published tracks
 * @returns {Promise<{ tracksCompleted: number, tracksTotal: number, resume: object|null }>}
 */
export async function computeSingleLanguageMenuStats(userId, lang, tracks) {
  const preferredView = userId ? readRoadmapGraphScopeForLanguage(userId, lang) : null;
  const prefersTrackOrder = preferredView === 'learningPath';

  const langTracks = tracks
    .filter((t) => trackMatchesLanguage(t, lang))
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
  const nodeLists = await Promise.all(langTracks.map((t) => roadmapService.loadTrackNodes(t.trackId)));

  const perTrack = [];
  let tracksTotal = 0;
  let tracksCompleted = 0;
  for (let i = 0; i < langTracks.length; i++) {
    const trackId = langTracks[i].trackId;
    const nodes = nodeLists[i];
    if (nodes.length === 0) continue;
    tracksTotal += 1;
    let prog = { completedNodeIds: [] };
    if (userId) {
      try {
        prog = await roadmapService.loadTrackProgress(userId, trackId);
      } catch {
        /* skip */
      }
    }
    const done = new Set(prog.completedNodeIds || []);
    const ordered = buildOrderedNodeGraph(nodes);
    const allDone = ordered.length > 0 && ordered.every((n) => done.has(n.id));
    if (allDone) tracksCompleted += 1;
    perTrack.push({ track: langTracks[i], ordered, done, allDone });
  }

  let singleTrackResume = null;
  for (const pts of perTrack) {
    if (pts.allDone) continue;
    const firstIncomplete = pts.ordered.find((n) => !pts.done.has(n.id));
    if (firstIncomplete) {
      const nodeIdx = pts.ordered.findIndex((n) => n.id === firstIncomplete.id);
      singleTrackResume = {
        kind: 'singleTrack',
        trackId: pts.track.trackId,
        nodeId: firstIncomplete.id,
        trackTitle: formatRoadmapTrackTitle(lang, pts.track.title, pts.track),
        nodeIndex: nodeIdx >= 0 ? nodeIdx + 1 : 1,
        nodeCount: pts.ordered.length,
        nodeTitle: firstIncomplete.title || 'Node'
      };
      break;
    }
  }

  const firstIncompleteTrackOrderIdx = perTrack.findIndex((p) => !p.allDone);
  let trackOrderResume = null;
  if (tracksTotal === 0) {
    trackOrderResume = null;
  } else if (firstIncompleteTrackOrderIdx < 0) {
    trackOrderResume = {
      kind: 'trackOrder',
      sectionTitle: `${formatLanguageDisplay(lang)} ${ROADMAP_SCOPE_LABEL}`,
      allTracksDone: true,
      trackTotal: tracksTotal
    };
  } else {
    const pts = perTrack[firstIncompleteTrackOrderIdx];
    const firstIncomplete = pts.ordered.find((n) => !pts.done.has(n.id));
    const nodeIdx = firstIncomplete
      ? pts.ordered.findIndex((n) => n.id === firstIncomplete.id)
      : -1;
    trackOrderResume = {
      kind: 'trackOrder',
      sectionTitle: `${formatLanguageDisplay(lang)} ${ROADMAP_SCOPE_LABEL}`,
      trackIndex: firstIncompleteTrackOrderIdx + 1,
      trackTotal: tracksTotal,
      trackId: pts.track.trackId,
      nodeId: firstIncomplete?.id ?? null,
      currentTrackTitle: formatRoadmapTrackTitle(lang, pts.track.title, pts.track),
      nodeTitle: firstIncomplete?.title || 'Node',
      nodeIndex: nodeIdx >= 0 ? nodeIdx + 1 : 1,
      nodeCount: pts.ordered.length,
      allTracksDone: false
    };
  }

  let resume = null;
  if (prefersTrackOrder && trackOrderResume) {
    resume = trackOrderResume;
  } else if (!prefersTrackOrder && singleTrackResume) {
    resume = singleTrackResume;
  }

  return {
    tracksCompleted,
    tracksTotal,
    resume
  };
}

/**
 * For each language with a roadmap cookbook, find the snippet (card) the user studied most
 * recently (`progress.lastReviewed` desc). Used by the language menu quick-start button to
 * jump straight back into study mode for that snippet.
 *
 * One Firestore read per user-deck collection + one progress collection scan + one card
 * read per resolved language (only the snippet whose `lastReviewed` is the latest in that
 * language is fetched, not every candidate row).
 *
 * @param {string|null} userId
 * @param {Array<{ trackId: string, title?: string, language?: string }>} tracks - published catalog
 * @returns {Promise<Map<string, {
 *   deckId: string,
 *   cardId: string,
 *   trackId: string|null,
 *   nodeId: string|null,
 *   trackTitle: string,
 *   nodeTitle: string,
 *   snippetTitle: string,
 *   language: string,
 *   lastReviewedMs: number
 * }>>} - keyed by normalized language key
 */
export async function loadRecentSnippetByLanguage(userId, tracks) {
  if (!userId) return new Map();

  let decksSnap;
  let progressRows;
  try {
    [decksSnap, progressRows] = await Promise.all([
      getDocs(collection(db, `users/${userId}/decks`)),
      new SpacedRepetition(userId).fetchAllCardProgressRows()
    ]);
  } catch {
    return new Map();
  }

  /** Map deckId → roadmap deck info (skip non-roadmap cookbooks so they don't shadow real roadmap rows). */
  const deckInfo = new Map();
  decksSnap.forEach((deckDoc) => {
    const data = deckDoc.data();
    const deckWithId = { id: deckDoc.id, ...data };
    if (!roadmapService.isRoadmapUserCookbook(deckWithId)) return;
    deckInfo.set(deckDoc.id, {
      deckId: deckDoc.id,
      trackId: getRoadmapTrackIdFromUserDeckData(data),
      nodeId: data.roadmapNodeId ?? null,
      nodeTitle: typeof data.roadmapNodeTitle === 'string' ? data.roadmapNodeTitle : '',
      language: typeof data.language === 'string' ? data.language : ''
    });
  });

  const trackById = new Map();
  (tracks || []).forEach((t) => {
    if (t && t.trackId != null) trackById.set(String(t.trackId), t);
  });

  /** Best (latest) progress row per language. */
  const bestByLang = new Map();
  for (const row of progressRows || []) {
    if (!row?.deckId || !row?.cardId || !row?.lastReviewed) continue;
    const reviewedAt = new Date(row.lastReviewed).getTime();
    if (!Number.isFinite(reviewedAt)) continue;
    const info = deckInfo.get(row.deckId);
    if (!info) continue;
    const langKey = normalizeRoadmapLanguageKey(info.language);
    if (!langKey) continue;
    const prev = bestByLang.get(langKey);
    if (!prev || reviewedAt > prev.lastReviewedMs) {
      bestByLang.set(langKey, {
        ...info,
        cardId: row.cardId,
        lastReviewedMs: reviewedAt
      });
    }
  }

  /** Fetch card title only for the winning snippet of each language (one read per language). */
  const result = new Map();
  await Promise.all(
    Array.from(bestByLang.entries()).map(async ([langKey, info]) => {
      let snippetTitle = '';
      try {
        const cardSnap = await getDoc(
          doc(db, `users/${userId}/decks/${info.deckId}/cards/${info.cardId}`)
        );
        if (cardSnap.exists()) {
          const rawTitle = cardSnap.data()?.title;
          snippetTitle = typeof rawTitle === 'string' ? rawTitle.trim() : '';
        }
      } catch {
        /* fall back to generic label below */
      }

      const track = info.trackId ? trackById.get(String(info.trackId)) : null;
      const rawTrackTitle = track?.title || '';
      const trackTitle = rawTrackTitle
        ? formatRoadmapTrackTitle(langKey, rawTrackTitle, track)
        : '';

      result.set(langKey, {
        deckId: info.deckId,
        cardId: info.cardId,
        trackId: info.trackId,
        nodeId: info.nodeId,
        trackTitle,
        nodeTitle: info.nodeTitle,
        snippetTitle: snippetTitle || 'Snippet',
        language: info.language,
        lastReviewedMs: info.lastReviewedMs
      });
    })
  );

  return result;
}

/**
 * @param {Map<string, { lastReviewedMs?: number }>|Record<string, { lastReviewedMs?: number }>|null|undefined} lastReviewedByLangKey
 * @param {string} lang
 * @returns {number}
 */
export function getLanguageLastStudiedMs(lastReviewedByLangKey, lang) {
  const key = normalizeRoadmapLanguageKey(lang);
  if (!key || !lastReviewedByLangKey) return 0;

  const entry =
    lastReviewedByLangKey instanceof Map
      ? lastReviewedByLangKey.get(key)
      : lastReviewedByLangKey[key];

  if (entry == null) return 0;
  if (typeof entry === 'number') return entry;
  return Number(entry.lastReviewedMs) || 0;
}

/**
 * Sort catalogue languages by most recently studied snippet (SRS lastReviewed desc).
 * Languages with no study history fall back to alphabetical order at the end.
 *
 * @param {string[]} languages
 * @param {Map<string, { lastReviewedMs?: number }>|Record<string, { lastReviewedMs?: number }>|null|undefined} lastReviewedByLangKey
 * @param {{ prioritizeLanguage?: string|null }} [options] - guest fallback: bump last opened roadmap language
 * @returns {string[]}
 */
export function sortLanguagesByRecentStudy(
  languages,
  lastReviewedByLangKey,
  { prioritizeLanguage = null } = {}
) {
  if (!Array.isArray(languages) || languages.length <= 1) {
    return Array.isArray(languages) ? [...languages] : [];
  }

  const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
  const priorityKey = prioritizeLanguage
    ? normalizeRoadmapLanguageKey(prioritizeLanguage)
    : '';

  return [...languages].sort((a, b) => {
    const ak = normalizeRoadmapLanguageKey(a);
    const bk = normalizeRoadmapLanguageKey(b);
    let aMs = getLanguageLastStudiedMs(lastReviewedByLangKey, a);
    let bMs = getLanguageLastStudiedMs(lastReviewedByLangKey, b);

    if (aMs === 0 && ak === priorityKey) aMs = 1;
    if (bMs === 0 && bk === priorityKey) bMs = 1;

    if (bMs !== aMs) return bMs - aMs;
    return collator.compare(formatLanguageDisplay(a), formatLanguageDisplay(b));
  });
}
